import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {PDFDocumentProxy, PDFDocumentLoadingTask} from 'pdfjs-dist';
import {DocxPreviewError, EmlPreviewError, identifyPreviewContent, loadPreviewPdf, PREVIEW_MAX_PIXELS, PREVIEW_TIMEOUT_MS, readPreviewBytes, XlsxPreviewError, type PreviewContent} from './fenzsafeDocumentPreview';
import {extractEmlMetadata, type EmlMetadata} from './fenzsafeEmlText';
import './FenzSafeDocumentViewer.css';

type Props = {name: string; loadFile: (signal: AbortSignal) => Promise<Blob>; onClose: () => void};

function formatEmlAddressList(addresses: EmlMetadata['from']): string {
  return addresses.map(address => (address.name ? `${address.name} <${address.address}>` : address.address)).join(', ');
}

/** The parent must key this button by authorization scope and document version. */
export function FenzSafeViewDocumentButton({name, loadFile, className, disabled}: Omit<Props, 'onClose'> & {className: string; disabled?: boolean}) {
  const [request, setRequest] = useState<Omit<Props, 'onClose'> | null>(null);
  return <>
    <button type="button" className={className} disabled={disabled} aria-label={`View document ${name}`} onClick={() => setRequest({name, loadFile})}>View document</button>
    {request && <FenzSafeDocumentViewer {...request} onClose={() => setRequest(null)} />}
  </>;
}

export function FenzSafeDocumentViewer({name, loadFile, onClose}: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const pdfTask = useRef<PDFDocumentLoadingTask | null>(null);
  const [content, setContent] = useState<PreviewContent | null>(null);
  const [metadata, setMetadata] = useState<EmlMetadata | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [pageText, setPageText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.showModal();
    return () => { if (opener?.isConnected) opener.focus(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let task: PDFDocumentLoadingTask | undefined;
    const timeout = window.setTimeout(() => {
      controller.abort();
      void task?.destroy();
      setError('The viewer took too long to open this file. Close it and try again, or use Download.');
      setBusy(false);
    }, PREVIEW_TIMEOUT_MS);
    void (async () => {
      const blob = await loadFile(controller.signal);
      if (controller.signal.aborted) return;
      const bytes = await readPreviewBytes(blob, controller.signal);
      if (controller.signal.aborted) return;
      const result = await identifyPreviewContent(bytes, name);
      if (controller.signal.aborted) return;
      if (result.kind === 'text' && name.toLowerCase().endsWith('.eml')) {
        // Best-effort and non-blocking: a metadata-extraction failure must never break the primary preview.
        // A private ArrayBuffer copy, matching identifyPreviewContent's own EML parse above.
        extractEmlMetadata(new Uint8Array(bytes).buffer).then(parsed => { if (!controller.signal.aborted) setMetadata(parsed); }).catch(() => {});
      }
      if (result.kind === 'pdf') {
        const library = await loadPreviewPdf();
        if (controller.signal.aborted) return;
        // Canvas/text only: no scripting, annotation, form, link or embedded-file layers.
        task = library.getDocument({data: result.bytes, disableAutoFetch: true, disableStream: true,
          maxImageSize: PREVIEW_MAX_PIXELS, isOffscreenCanvasSupported: false});
        pdfTask.current = task;
        task.onPassword = () => { void task?.destroy(); };
        const document = await task.promise;
        if (controller.signal.aborted) return;
        if (document.numPages > 500) throw new Error('preview_page_limit');
        setPdf(document);
      }
      if (!controller.signal.aborted) { setContent(result); setBusy(result.kind !== 'text'); }
    })().catch((caught: unknown) => {
      if (!controller.signal.aborted) {
        setError(caught instanceof DocxPreviewError
          ? 'This Word document cannot be previewed here. It may be corrupt or password-protected. Use Download to open the original.'
          : caught instanceof EmlPreviewError
            ? 'This email file cannot be previewed here. It may be malformed, protected, or too large. Use Download to open the original.'
            : caught instanceof XlsxPreviewError
              ? 'Excel files cannot be previewed here yet. Use Download to open the original in a spreadsheet application.'
              : 'This file could not be displayed. It may be unavailable, protected, damaged, or unsupported. Close the viewer to retry or download the original.');
        setBusy(false);
        void task?.destroy();
      }
    }).finally(() => window.clearTimeout(timeout));
    return () => { controller.abort(); window.clearTimeout(timeout); void task?.destroy(); if (pdfTask.current === task) pdfTask.current = null; };
  }, [loadFile, name]);

  useEffect(() => {
    if (!content || content.kind === 'text' || !canvas.current) return;
    let active = true;
    let renderTask: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined;
    let bitmap: ImageBitmap | undefined;
    const target = canvas.current;
    target.width = target.height = 0; // Remove the previous page before a new asynchronous render.
    setPageText('');
    setBusy(true);
    const fail = () => {
      if (!active) return;
      active = false;
      renderTask?.cancel();
      // Cancellation of a render is insufficient while getPage/text parsing is pending.
      // Terminal failure tears down the worker immediately, without waiting for Close.
      void pdfTask.current?.destroy();
      bitmap?.close();
      target.width = target.height = 0;
      setError('This page could not be displayed. Close the viewer and try again, or use Download.');
      setBusy(false);
    };
    const timeout = window.setTimeout(fail, PREVIEW_TIMEOUT_MS);
    void (async () => {
      if (content.kind === 'pdf' && pdf) {
        const pdfPage = await pdf.getPage(page);
        if (!active) return;
        try {
          const base = pdfPage.getViewport({scale: 1});
          if (!Number.isFinite(base.width * base.height) || base.width <= 0 || base.height <= 0) throw new Error('invalid_page');
          const scale = Math.min(1.5, 1600 / base.width, 4000 / base.height, Math.sqrt(4_000_000 / (base.width * base.height)));
          const viewport = pdfPage.getViewport({scale});
          target.width = Math.max(1, Math.floor(viewport.width));
          target.height = Math.max(1, Math.floor(viewport.height));
          renderTask = pdfPage.render({canvas: target, viewport, annotationMode: 0});
          await renderTask.promise;
          if (!active) return;
          const text = await pdfPage.getTextContent();
          if (!active) return;
          setPageText(text.items.map(item => 'str' in item ? item.str : '').join(' ').slice(0, 100_000));
        } finally { pdfPage.cleanup(); }
      } else if (content.kind === 'image') {
        bitmap = await createImageBitmap(new Blob([content.bytes as Uint8Array<ArrayBuffer>], {type: content.mime}));
        if (!active) { bitmap.close(); return; }
        if (bitmap.width * bitmap.height > PREVIEW_MAX_PIXELS) throw new Error('image_limit');
        const scale = Math.min(1, 1600 / bitmap.width, 4000 / bitmap.height);
        target.width = Math.max(1, Math.floor(bitmap.width * scale));
        target.height = Math.max(1, Math.floor(bitmap.height * scale));
        const context = target.getContext('2d');
        if (!context) throw new Error('canvas_unavailable');
        context.drawImage(bitmap, 0, 0, target.width, target.height);
        bitmap.close();
      }
      if (active) setBusy(false);
    })().catch(fail).finally(() => window.clearTimeout(timeout));
    return () => { active = false; window.clearTimeout(timeout); renderTask?.cancel(); bitmap?.close(); target.width = target.height = 0; };
  }, [content, pdf, page]);

  return createPortal(<dialog ref={dialog} className="fsdv-dialog" aria-labelledby="fsdv-title"
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <header className="fsdv-header">
      <div><h2 id="fsdv-title">{name}</h2><p>Read-only view · Your original file stays unchanged.</p></div>
      <button type="button" onClick={onClose}>Close viewer</button>
    </header>
    {pdf && !error && <nav className="fsdv-pages" aria-label="Document pages">
      <button type="button" disabled={page === 1 || busy} onClick={() => setPage(n => n - 1)}>Previous page</button>
      <span aria-live="polite">Page {page} of {pdf.numPages}</span>
      <button type="button" disabled={page === pdf.numPages || busy} onClick={() => setPage(n => n + 1)}>Next page</button>
    </nav>}
    {busy && !error && <p role="status">Opening document…</p>}
    {error ? <p role="alert">{error}</p> : <div className="fsdv-content" aria-busy={busy}>
      {content?.kind === 'text' ? <pre>{content.text}</pre> : <canvas ref={canvas} role="img" aria-label={`${name}${pdf ? `, page ${page}` : ''}`} />}
      {metadata && (metadata.subject || metadata.from.length > 0 || metadata.to.length > 0 || metadata.cc.length > 0 || metadata.bcc.length > 0 || metadata.date || metadata.messageId || metadata.attachments.length > 0) && (
        <div className="fsdv-eml-metadata">
          <h3>Message details</h3>
          <dl>
            {metadata.subject && <div className="fsdv-eml-metadata-row"><dt>Subject</dt><dd>{metadata.subject}</dd></div>}
            {metadata.from.length > 0 && <div className="fsdv-eml-metadata-row"><dt>From</dt><dd>{formatEmlAddressList(metadata.from)}</dd></div>}
            {metadata.to.length > 0 && <div className="fsdv-eml-metadata-row"><dt>To</dt><dd>{formatEmlAddressList(metadata.to)}</dd></div>}
            {metadata.cc.length > 0 && <div className="fsdv-eml-metadata-row"><dt>Cc</dt><dd>{formatEmlAddressList(metadata.cc)}</dd></div>}
            {metadata.bcc.length > 0 && <div className="fsdv-eml-metadata-row"><dt>Bcc</dt><dd>{formatEmlAddressList(metadata.bcc)}</dd></div>}
            {metadata.date && <div className="fsdv-eml-metadata-row"><dt>Date</dt><dd>{metadata.date}</dd></div>}
            {metadata.messageId && <div className="fsdv-eml-metadata-row"><dt>Message-ID</dt><dd>{metadata.messageId}</dd></div>}
          </dl>
          {metadata.attachments.length > 0 && <details>
            <summary>Original attachments ({metadata.attachments.length})</summary>
            <ul>
              {metadata.attachments.map((attachment, index) => (
                <li key={index}>{attachment.filename ?? '(unnamed attachment)'}{attachment.sha256 ? ` — SHA-256: ${attachment.sha256}` : ''}</li>
              ))}
            </ul>
          </details>}
        </div>
      )}
      {pdf && !busy && <details><summary>Readable text for this page</summary><pre>{pageText || 'No text layer is available for this page. The page image is shown above.'}</pre></details>}
    </div>}
  </dialog>, document.body);
}
